import { BRANDING, sanitizeErpBranding } from "app/utils/branding";

export class OdooApiError extends Error {
  code?: string;
  httpStatus?: number;

  constructor(message: string, code?: string, httpStatus?: number) {
    super(message);
    this.name = "OdooApiError";
    this.code = code;
    this.httpStatus = httpStatus;
  }

  get isNotFound(): boolean {
    return (
      this.httpStatus === 404 ||
      this.code === "not_found_object_in_odoo" ||
      this.code === "route_not_found"
    );
  }

  get isAccessDenied(): boolean {
    return this.httpStatus === 403 || this.code === "access_denied";
  }

  get isServerUnavailable(): boolean {
    return this.code === "network_error";
  }

  get isModelUnavailable(): boolean {
    return (
      this.code === "model_not_available" ||
      this.code === "method_not_exist_in_odoo"
    );
  }
}

const ERROR_CODE_USER_MESSAGES: Record<string, string> = {
  not_found_object_in_odoo: "This record no longer exists.",
  route_not_found: "Route not found.",
  access_denied: "You don't have permission to perform this action.",
  model_not_available:
    "This feature is not available on the server. Contact your administrator.",
  method_not_exist_in_odoo:
    "This action is not available on the server. Contact your administrator.",
  internal_server_error: "Something went wrong on the server. Please try again.",
  network_error: "Cannot reach the server. Check your connection and try again.",
  no_active_session: BRANDING.noActiveSession,
  user_authentication_failed: "Invalid username or password.",
  empty_username_or_password: "Please enter your username and password.",
};

export function formatOdooApiErrorMessage(
  error?: string,
  errorDescrip?: string,
  fallback = "Request failed."
): string {
  if (error && ERROR_CODE_USER_MESSAGES[error] && !errorDescrip) {
    return ERROR_CODE_USER_MESSAGES[error];
  }

  let raw = (errorDescrip || error || fallback).trim();

  const wrappedMatch = raw.match(
    /^Not (?:called method|updated object|created object|deleted object|read object) in Odoo! ERROR: (.+)$/s
  );
  if (wrappedMatch) {
    raw = wrappedMatch[1].trim();
  }

  const odooExceptionMatch = raw.match(
    /^(?:UserError|ValidationError|AccessError)\(['"]([\s\S]*)['"]\)$/
  );
  if (odooExceptionMatch) {
    return odooExceptionMatch[1].replace(/\\n/g, "\n").replace(/\\'/g, "'");
  }

  if (error && ERROR_CODE_USER_MESSAGES[error]) {
    const genericPrefixes = [
      "Not called method in Odoo!",
      "Not updated object in Odoo!",
      "Not read object in Odoo!",
      "Not created object in Odoo!",
      "Not deleted object in Odoo!",
    ];
    if (genericPrefixes.some((prefix) => raw.startsWith(prefix))) {
      return ERROR_CODE_USER_MESSAGES[error];
    }
  }

  if (raw.includes("AccessError")) {
    return ERROR_CODE_USER_MESSAGES.access_denied;
  }

  return sanitizeErpBranding(raw);
}

export function getApiErrorMessage(
  error: unknown,
  fallback = "Request failed."
): string {
  if (error instanceof OdooApiError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message || fallback;
  }
  return fallback;
}

export function isNotFoundError(error: unknown): boolean {
  return error instanceof OdooApiError && error.isNotFound;
}

export function isAccessDeniedError(error: unknown): boolean {
  return error instanceof OdooApiError && error.isAccessDenied;
}

export function createOdooApiError(
  parsed: { error?: string; error_descrip?: string },
  httpStatus: number,
  fallback = "Request failed."
): OdooApiError {
  const message = formatOdooApiErrorMessage(
    parsed.error,
    parsed.error_descrip,
    fallback
  );
  return new OdooApiError(message, parsed.error, httpStatus);
}

export function createNetworkError(cause?: unknown): OdooApiError {
  const detail =
    cause instanceof Error && cause.message ? ` (${cause.message})` : "";
  return new OdooApiError(
    `${ERROR_CODE_USER_MESSAGES.network_error}${detail}`,
    "network_error"
  );
}
